import * as msal from '@azure/msal-browser';

const msalConfig = {
  auth: {
    clientId: 'e15609bd-7db8-438f-9d2d-5746f09fb79e',
    authority: 'https://login.microsoftonline.com/50418cc1-3ee5-4310-99ae-28c130667818',
    redirectUri: window.location.origin
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false
  }
};

const loginRequest = {
  scopes: ['https://abctaxadvisory.sharepoint.com/AllSites.FullControl']
};

const msalInstance = new msal.PublicClientApplication(msalConfig);

const SHAREPOINT_CONFIG = {
  siteUrl: 'https://abctaxadvisory.sharepoint.com/sites/TaxClientManagement',
  clientsListName: 'Client Master',
  tasksListName: 'Client Tasks',
  templatesListName: 'Task Template',
  clientMessagesListName: 'Client Messages',
  broadcastMessagesListName: 'Broadcast Messages',
  invitationsListName: 'Client Invitations',
  appointmentsListName: 'Client Appointments',       // ✅ NEW
  filesLibrary: 'Client Folders',
  fileSubfolders: ['Completed Returns', 'Correspondence', 'Uploaded Documents']
};

// VERIFIED __metadata type names (from ListItemEntityTypeFullName):
// Client Master        → SP.Data.Client_x0020_MasterListItem
// Client Tasks         → SP.Data.Client_x0020_TasksListItem
// Client Messages      → SP.Data.Client_x0020_MessagesListItem
// Broadcast Messages   → SP.Data.Client_x0020_MessagesListItem
// Task Template        → SP.Data.Client_x0020_TasksListItem
// Client Invitations   → SP.Data.Client_x0020_InvitationsListItem
// Client Appointments  → SP.Data.Client_x0020_AppointmentsListItem  ✅ NEW

// VERIFIED field types:
// Broadcast Messages: SentStatus=Choice(6), SentCount=Integer(9), RecipientType=Choice(6), MessageSubject=Text(2)
// Task Template: IsActive=Boolean(8), EntityType=Choice(6), TasksJSON=MultilineText(3)
// Client Invitations: InviteToken=Text, Status=Choice, ExpiryDate=DateTime, Email=Text, SentBy=Text
// Client Messages: FileUrl=Text, FileName=Text, FileSize=Number  ✅ NEW columns needed
// Client Appointments: Title=Text, ClientName=Text, AppointmentDate=DateTime,
//   StartTime=Text, EndTime=Text, MeetingType=Choice, AssigneeName=Text,
//   Status=Choice, Notes=MultilineText  ✅ NEW

class SharePointService {
  constructor() {
    this.baseUrl = `${SHAREPOINT_CONFIG.siteUrl}/_api/web/lists`;
    this.msalInstance = msalInstance;
    this.accessToken = null;
    this.headers = {
      'Accept': 'application/json;odata=verbose',
      'Content-Type': 'application/json;odata=verbose'
    };
  }

  // ========== AUTHENTICATION ==========
  async initialize() {
    await this.msalInstance.initialize();
    await this.handleRedirectPromise();
  }

  async handleRedirectPromise() {
    try {
      const response = await this.msalInstance.handleRedirectPromise();
      if (response) {
        this.accessToken = response.accessToken;
        return response.account;
      }
      return null;
    } catch (error) {
      console.error('Error handling redirect:', error);
      throw error;
    }
  }

  async login() {
    try {
      await this.msalInstance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  async getAccessToken() {
    try {
      const accounts = this.msalInstance.getAllAccounts();
      if (accounts.length === 0) throw new Error('No accounts found. Please login.');
      const request = { ...loginRequest, account: accounts[0] };
      const response = await this.msalInstance.acquireTokenSilent(request);
      this.accessToken = response.accessToken;
      return response.accessToken;
    } catch (error) {
      if (error instanceof msal.InteractionRequiredAuthError) {
        await this.msalInstance.acquireTokenRedirect(loginRequest);
      }
      throw error;
    }
  }

  // Acquires a Graph API token with Mail.Send scope
  async getGraphToken() {
    try {
      const accounts = this.msalInstance.getAllAccounts();
      if (accounts.length === 0) throw new Error('No accounts found.');
      const response = await this.msalInstance.acquireTokenSilent({
        scopes: ['https://graph.microsoft.com/Mail.Send'],
        account: accounts[0]
      });
      return response.accessToken;
    } catch (error) {
      if (error instanceof msal.InteractionRequiredAuthError) {
        await this.msalInstance.acquireTokenRedirect({
          scopes: ['https://graph.microsoft.com/Mail.Send']
        });
      }
      throw error;
    }
  }

  async logout() {
    // Always clear client session
    sessionStorage.removeItem('clientSession');
    // Only do MSAL logout if admin is authenticated
    if (this.isAuthenticated()) {
      try {
        await this.msalInstance.logoutRedirect();
      } catch (error) {
        console.error('Logout error:', error);
      }
    } else {
      // Client OTP logout — just redirect to login
      window.location.href = '/login';
    }
  }

  getCurrentUser() {
    const accounts = this.msalInstance.getAllAccounts();
    return accounts.length > 0 ? accounts[0] : null;
  }

  isAuthenticated() {
    return this.msalInstance.getAllAccounts().length > 0;
  }

  async getAuthHeaders() {
    const token = await this.getAccessToken();
    return { ...this.headers, 'Authorization': `Bearer ${token}` };
  }

  async getFormDigest() {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${SHAREPOINT_CONFIG.siteUrl}/_api/contextinfo`, {
        method: 'POST',
        headers
      });
      const data = await response.json();
      return data.d.GetContextWebInformation.FormDigestValue;
    } catch (error) {
      console.error('Error getting form digest:', error);
      throw error;
    }
  }

  _escapeOData(value) {
    return (value || '').replace(/'/g, "''");
  }

  _parseJSON(str, fallback = null) {
    try {
      return JSON.parse(str);
    } catch {
      return fallback;
    }
  }

  // ========== CLIENT MASTER ==========
  // VERIFIED internal names:
  // Title, ClientEmail, ClientPhone, ServiceType, Status,
  // TaxYear, Deadline, OneDriveFolderPath, PortalAccessSent,
  // Address, TaxID, EntityType, AssignedCPA
  async getClients() {
    try {
      const headers = await this.getAuthHeaders();
      const selectFields = [
        'Id', 'Title', 'ClientEmail', 'ClientPhone', 'ServiceType',
        'Status', 'TaxYear', 'FilingDeadline', 'OneDriveFolderPath', 'PortalAccessSent',
        'Address', 'TaxID', 'EntityType', 'AssignedCPA',
        'Modified', 'Author/Title'
      ].join(',');
      const listUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.clientsListName}')/items` +
        `?$select=${selectFields}&$expand=Author&$orderby=Title asc&$top=500`;
      const response = await fetch(listUrl, { method: 'GET', headers });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      return data.d.results.map(item => ({
        ...item,
        Email: item.ClientEmail || '',
        Phone: item.ClientPhone || '',
        CompanyName: item.EntityType || item.ServiceType || 'Individual',
        AuthorName: item.Author?.Title || 'Unassigned'
      }));
    } catch (error) {
      console.error('Error fetching clients:', error);
      throw error;
    }
  }

  async getClientById(clientId) {
    try {
      const headers = await this.getAuthHeaders();
      const selectFields = [
        'Id', 'Title', 'ClientEmail', 'ClientPhone', 'ServiceType',
        'Status', 'TaxYear', 'FilingDeadline', 'OneDriveFolderPath', 'PortalAccessSent',
        'Address', 'TaxID', 'EntityType', 'AssignedCPA',
        'Modified', 'Author/Title'
      ].join(',');
      const itemUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.clientsListName}')/items(${clientId})` +
        `?$select=${selectFields}&$expand=Author`;
      const response = await fetch(itemUrl, { method: 'GET', headers });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      const item = data.d;
      return {
        ...item,
        Email: item.ClientEmail || '',
        Phone: item.ClientPhone || '',
        CompanyName: item.EntityType || item.ServiceType || 'Individual',
        AuthorName: item.Author?.Title || 'Unassigned'
      };
    } catch (error) {
      console.error('Error fetching client:', error);
      throw error;
    }
  }

  async getClientByEmail(email) {
  try {
    const headers = await this.getAuthHeaders();
    const safeEmail = this._escapeOData(email.toLowerCase().trim());
    const selectFields = [
      'Id', 'Title', 'ClientEmail', 'ClientPhone', 'ServiceType',
      'Status', 'TaxYear', 'FilingDeadline', 'EntityType', 'AssignedCPA',
      'PortalAccessSent', 'OneDriveFolderPath'
    ].join(',');
    const listUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.clientsListName}')/items` +
      `?$select=${selectFields}` +
      `&$filter=ClientEmail eq '${safeEmail}'` +
      `&$top=1`;
    const response = await fetch(listUrl, { method: 'GET', headers });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    const results = data.d?.results || [];
    return results.length > 0 ? results[0] : null;
  } catch (error) {
    console.error('Error fetching client by email:', error);
    return null;
  }
}

  async addClient(clientData) {
    try {
      const headers = await this.getAuthHeaders();
      const formDigest = await this.getFormDigest();
      const listUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.clientsListName}')/items`;
      const itemPayload = {
        __metadata: { type: 'SP.Data.Client_x0020_MasterListItem' },
        Title: clientData.name,
        ClientEmail: clientData.email || '',
        ClientPhone: clientData.phone || '',
        TaxYear: clientData.taxYear || new Date().getFullYear().toString(),
        ServiceType: clientData.serviceType || 'Individual',
        EntityType: clientData.serviceType || 'Individual',
        Status: 'New',
        FilingDeadline: clientData.deadline || null,
        AssignedCPA: clientData.assignedCPA || '',
        Address: clientData.address || '',
        TaxID: clientData.taxId || '',
        OneDriveFolderPath: `/sites/TaxClientManagement/Client Folders/${clientData.name}`,
        PortalAccessSent: false
      };
      const response = await fetch(listUrl, {
        method: 'POST',
        headers: { ...headers, 'X-RequestDigest': formDigest },
        body: JSON.stringify(itemPayload)
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('SharePoint error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.d;
    } catch (error) {
      console.error('Error adding client:', error);
      throw error;
    }
  }

  async updateClient(clientId, clientData) {
    try {
      const headers = await this.getAuthHeaders();
      const formDigest = await this.getFormDigest();
      const itemUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.clientsListName}')/items(${clientId})`;
      const itemPayload = {
        __metadata: { type: 'SP.Data.Client_x0020_MasterListItem' }
      };
      if (clientData.name) itemPayload.Title = clientData.name;
      if (clientData.email !== undefined) itemPayload.ClientEmail = clientData.email;
      if (clientData.phone !== undefined) itemPayload.ClientPhone = clientData.phone;
      if (clientData.taxYear) itemPayload.TaxYear = clientData.taxYear;
      if (clientData.serviceType) itemPayload.ServiceType = clientData.serviceType;
      if (clientData.entityType || clientData.serviceType) itemPayload.EntityType = clientData.entityType || clientData.serviceType;
      if (clientData.status) itemPayload.Status = clientData.status;
      if (clientData.deadline !== undefined) itemPayload.FilingDeadline = clientData.deadline;
      if (clientData.assignedCPA) itemPayload.AssignedCPA = clientData.assignedCPA;
      if (clientData.address !== undefined) itemPayload.Address = clientData.address;
      if (clientData.taxId !== undefined) itemPayload.TaxID = clientData.taxId;
      if (clientData.portalAccessSent !== undefined) itemPayload.PortalAccessSent = clientData.portalAccessSent;
      if (clientData.oneDriveFolderPath) itemPayload.OneDriveFolderPath = clientData.oneDriveFolderPath;
      const response = await fetch(itemUrl, {
        method: 'POST',
        headers: {
          ...headers,
          'X-RequestDigest': formDigest,
          'X-HTTP-Method': 'MERGE',
          'IF-MATCH': '*'
        },
        body: JSON.stringify(itemPayload)
      });
      if (!response.ok && response.status !== 204) {
        const errorText = await response.text();
        console.error('SharePoint error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return { success: true };
    } catch (error) {
      console.error('Error updating client:', error);
      throw error;
    }
  }

  async deleteClient(clientId) {
    try {
      const headers = await this.getAuthHeaders();
      const formDigest = await this.getFormDigest();
      const itemUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.clientsListName}')/items(${clientId})`;
      const response = await fetch(itemUrl, {
        method: 'POST',
        headers: {
          ...headers,
          'X-RequestDigest': formDigest,
          'X-HTTP-Method': 'DELETE',
          'IF-MATCH': '*'
        }
      });
      if (!response.ok && response.status !== 204) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return true;
    } catch (error) {
      console.error('Error deleting client:', error);
      throw error;
    }
  }

  async getEntityTypeChoices() {
    try {
      const headers = await this.getAuthHeaders();
      const url = `${SHAREPOINT_CONFIG.siteUrl}/_api/web/lists/getbytitle('Client Master')` +
        `/fields/getbyinternalnameortitle('ServiceType')/Choices`;
      const res = await fetch(url, { method: 'GET', headers });
      if (!res.ok) throw new Error('Choices fetch failed: ' + res.status);
      const data = await res.json();
      return (data.d && data.d.Choices && data.d.Choices.results) || data.value || [];
    } catch (error) {
      console.error('Error fetching entity type choices:', error);
      return [];
    }
  }

  // ========== CLIENT TASKS ==========
  // VERIFIED internal names:
  // Title, ClientName, TaskStatus, Priority, Category,
  // AssigneeName, DueDate, Description, TemplateId
  // VERIFIED type: SP.Data.Client_x0020_TasksListItem
  async getClientTasks(clientId) {
    try {
      const headers = await this.getAuthHeaders();
      const client = await this.getClientById(clientId);
      const clientName = this._escapeOData(client.Title);
      const selectFields = [
        'Id', 'Title', 'ClientName', 'TaskStatus', 'Priority',
        'Category', 'AssigneeName', 'DueDate', 'Description',
        'TemplateId', 'Created'
      ].join(',');
      const listUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.tasksListName}')/items` +
        `?$select=${selectFields}` +
        `&$filter=ClientName eq '${clientName}'` +
        `&$orderby=Created desc&$top=200`;
      const response = await fetch(listUrl, { method: 'GET', headers });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      return data.d.results;
    } catch (error) {
      console.error('Error fetching client tasks:', error);
      return [];
    }
  }

  // ═══════════════════════════════════════════
  // CLIENT SESSION HELPERS
  // ═══════════════════════════════════════════
  _getClientSession() {
    try {
      const token = sessionStorage.getItem('clientSession');
      if (!token) return null;
      // Decode payload (first segment) for expiry check — no secret needed client-side
      const [data] = token.split('.');
      if (!data) return null;
      const payload = JSON.parse(atob(data.replace(/-/g, '+').replace(/_/g, '/')));
      if (!payload.exp || Date.now() > payload.exp) {
        sessionStorage.removeItem('clientSession');
        return null;
      }
      if (this.isAuthenticated()) return null; // admin session takes priority
      return token; // return the raw token string
    } catch { return null; }
  }

  async _clientFn(action, extra = {}) {
    const sessionToken = this._getClientSession();
    if (!sessionToken) throw new Error('No client session');
    const res = await fetch('/.netlify/functions/client-data', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, sessionToken, ...extra })
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'Function error');
    return data.data;
  }

  async getClientTasksByName(clientName) {
    if (this._getClientSession()) {
      try { return await this._clientFn('getTasks'); }
      catch (e) { console.error('getTasks error:', e); return []; }
    }
    try {
      const headers = await this.getAuthHeaders();
      const safeName = this._escapeOData(clientName);
      const selectFields = [
        'Id', 'Title', 'ClientName', 'TaskStatus', 'Priority',
        'Category', 'AssigneeName', 'DueDate', 'Description',
        'TemplateId', 'Created', 'Modified'
      ].join(',');
      const listUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.tasksListName}')/items` +
        `?$select=${selectFields}` +
        `&$filter=ClientName eq '${safeName}'` +
        `&$orderby=Created desc&$top=200`;
      const response = await fetch(listUrl, { method: 'GET', headers });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      return data.d.results;
    } catch (error) {
      console.error('Error fetching client tasks by name:', error);
      return [];
    }
  }

  async getAllTasks() {
    try {
      const headers = await this.getAuthHeaders();
      const selectFields = [
        'Id', 'Title', 'ClientName', 'TaskStatus', 'Priority',
        'Category', 'AssigneeName', 'DueDate', 'Description',
        'TemplateId', 'Created'
      ].join(',');
      const listUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.tasksListName}')/items` +
        `?$select=${selectFields}&$orderby=Created desc&$top=500`;
      const response = await fetch(listUrl, { method: 'GET', headers });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      return data.d.results;
    } catch (error) {
      console.error('Error fetching all tasks:', error);
      return [];
    }
  }

  async addTask(taskData) {
    try {
      const headers = await this.getAuthHeaders();
      const formDigest = await this.getFormDigest();
      const listUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.tasksListName}')/items`;
      const itemPayload = {
        __metadata: { type: 'SP.Data.Client_x0020_TasksListItem' },
        Title: taskData.title,
        ClientName: taskData.clientName || '',
        TaskStatus: taskData.status || 'To-Do',
        Priority: taskData.priority || 'Normal',
        Category: taskData.category || taskData.documentType || 'General',
        AssigneeName: taskData.assignee || '',
        DueDate: taskData.dueDate || null,
        Description: taskData.description || '',
        TemplateId: taskData.templateId || 0
      };
      const response = await fetch(listUrl, {
        method: 'POST',
        headers: { ...headers, 'X-RequestDigest': formDigest },
        body: JSON.stringify(itemPayload)
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('SharePoint error:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.d;
    } catch (error) {
      console.error('Error adding task:', error);
      throw error;
    }
  }

  async addMultipleTasks(tasks) {
    try {
      const results = await Promise.all(tasks.map(task => this.addTask(task)));
      return results;
    } catch (error) {
      console.error('Error adding multiple tasks:', error);
      throw error;
    }
  }

  async updateTaskStatus(taskId, newStatus) {
    if (this._getClientSession()) {
      return await this._clientFn('updateTaskStatus', { taskId, newStatus });
    }
    try {
      const headers = await this.getAuthHeaders();
      const formDigest = await this.getFormDigest();
      const itemUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.tasksListName}')/items(${taskId})`;
      const response = await fetch(itemUrl, {
        method: 'POST',
        headers: {
          ...headers,
          'X-RequestDigest': formDigest,
          'X-HTTP-Method': 'MERGE',
          'IF-MATCH': '*'
        },
        body: JSON.stringify({
          __metadata: { type: 'SP.Data.Client_x0020_TasksListItem' },
          TaskStatus: newStatus
        })
      });
      if (!response.ok && response.status !== 204) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return true;
    } catch (error) {
      console.error('Error updating task status:', error);
      throw error;
    }
  }

  async deleteTask(taskId) {
    try {
      const headers = await this.getAuthHeaders();
      const formDigest = await this.getFormDigest();
      const itemUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.tasksListName}')/items(${taskId})`;
      const response = await fetch(itemUrl, {
        method: 'POST',
        headers: {
          ...headers,
          'X-RequestDigest': formDigest,
          'X-HTTP-Method': 'DELETE',
          'IF-MATCH': '*'
        }
      });
      if (!response.ok && response.status !== 204) {
        const errorText = await response.text();
        console.error('Delete task SharePoint error:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return true;
    } catch (error) {
      console.error('Error deleting task:', error);
      throw error;
    }
  }

  // ========== TASK TEMPLATES ==========
  // VERIFIED type: SP.Data.Client_x0020_TasksListItem
  async getTemplates() {
    // Route through Netlify when in client session (avoids CORS on localhost)
    if (this._getClientSession()) {
      try { return await this._clientFn('getTemplates'); }
      catch (e) { console.error('getTemplates error:', e); return []; }
    }
    try {
      const headers = await this.getAuthHeaders();
      const listUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.templatesListName}')/items` +
        `?$select=Id,Title,TasksJSON,EntityType,Description,IsActive` +
        `&$orderby=Title asc&$top=100`;
      const response = await fetch(listUrl, { method: 'GET', headers });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      return data.d.results
        .filter(tpl => tpl.IsActive !== false)
        .map(tpl => ({
          id: tpl.Id,
          name: tpl.Title,
          entityType: tpl.EntityType || 'Individual',
          description: tpl.Description || '',
          tasks: this._parseJSON(tpl.TasksJSON, [])
        }));
    } catch (error) {
      console.error('Error fetching templates:', error);
      return [];
    }
  }

  async saveTemplate(templateData) {
    try {
      const headers = await this.getAuthHeaders();
      const formDigest = await this.getFormDigest();
      const listUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.templatesListName}')/items`;
      const itemPayload = {
        __metadata: { type: 'SP.Data.Task_x0020_TemplateListItem' },
        Title: templateData.name,
        TasksJSON: JSON.stringify(templateData.tasks || []),
        EntityType: templateData.entityType || 'Individual',
        Description: templateData.description || '',
        IsActive: true
      };
      const response = await fetch(listUrl, {
        method: 'POST',
        headers: { ...headers, 'X-RequestDigest': formDigest },
        body: JSON.stringify(itemPayload)
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('SharePoint error saving template:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.d;
    } catch (error) {
      console.error('Error saving template:', error);
      throw error;
    }
  }

  async updateTemplate(templateId, templateData) {
    try {
      const headers = await this.getAuthHeaders();
      const formDigest = await this.getFormDigest();
      const itemUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.templatesListName}')/items(${templateId})`;
      const itemPayload = {
        __metadata: { type: 'SP.Data.Task_x0020_TemplateListItem' },
        Title: templateData.name,
        TasksJSON: JSON.stringify(templateData.tasks || []),
        EntityType: templateData.entityType || 'Individual',
        Description: templateData.description || '',
        IsActive: true
      };
      const response = await fetch(itemUrl, {
        method: 'POST',
        headers: {
          ...headers,
          'X-RequestDigest': formDigest,
          'X-HTTP-Method': 'MERGE',
          'IF-MATCH': '*'
        },
        body: JSON.stringify(itemPayload)
      });
      if (!response.ok && response.status !== 204) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return true;
    } catch (error) {
      console.error('Error updating template:', error);
      throw error;
    }
  }

  async deleteTemplate(templateId) {
    try {
      const headers = await this.getAuthHeaders();
      const formDigest = await this.getFormDigest();
      const itemUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.templatesListName}')/items(${templateId})`;
      const response = await fetch(itemUrl, {
        method: 'POST',
        headers: {
          ...headers,
          'X-RequestDigest': formDigest,
          'X-HTTP-Method': 'DELETE',
          'IF-MATCH': '*'
        }
      });
      if (!response.ok && response.status !== 204) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return true;
    } catch (error) {
      console.error('Error deleting template:', error);
      throw error;
    }
  }

  // ========== CLIENT MESSAGES ==========
  // VERIFIED internal names:
  // Title, Message, ClientName, Sender, SenderType,
  // IsRead, ConversationId, SendEmail,
  // FileUrl, FileName, FileSize  ✅ NEW columns
  // VERIFIED type: SP.Data.Client_x0020_MessagesListItem
  async getClientMessages(clientName) {
    if (this._getClientSession()) {
      try { return await this._clientFn('getMessages'); }
      catch (e) { console.error('getMessages error:', e); return []; }
    }
    try {
      const headers = await this.getAuthHeaders();
      const safeName = this._escapeOData(clientName);
      const listUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.clientMessagesListName}')/items` +
        `?$select=Id,Title,Message,ClientName,Sender,SenderType,IsRead,ConversationId,SendEmail,FileUrl,FileSize,Created` +
        `&$filter=ClientName eq '${safeName}'` +
        `&$orderby=Created asc&$top=500`;
      const response = await fetch(listUrl, { method: 'GET', headers });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      return data.d.results;
    } catch (error) {
      console.error('Error fetching messages:', error);
      return [];
    }
  }

  async getAllMessages() {
    try {
      const headers = await this.getAuthHeaders();
      const listUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.clientMessagesListName}')/items` +
        `?$select=Id,Title,Message,ClientName,Sender,SenderType,IsRead,ConversationId,FileUrl,FileSize,Created` +
        `&$orderby=Created desc&$top=500`;
      const response = await fetch(listUrl, { method: 'GET', headers });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      return data.d.results;
    } catch (error) {
      console.error('Error fetching all messages:', error);
      return [];
    }
  }

  async sendClientMessage(messageData) {
    if (this._getClientSession()) {
      return await this._clientFn('sendMessage', {
        message: messageData.message,
        subject: messageData.subject,
        conversationId: messageData.conversationId,
        driveItemId: messageData.driveItemId || null,
        driveId: messageData.driveId || null,
        fileSize: messageData.fileSize || null
      });
    }
    try {
      const headers = await this.getAuthHeaders();
      const formDigest = await this.getFormDigest();
      const listUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.clientMessagesListName}')/items`;
      const conversationId = messageData.conversationId ||
        `${messageData.clientName}_${Date.now()}`;
      const itemPayload = {
        __metadata: { type: 'SP.Data.Client_x0020_MessagesListItem' },
        Title: messageData.subject || '',
        Message: messageData.message || '',
        ClientName: messageData.clientName,
        Sender: messageData.sender || 'Admin',
        SenderType: messageData.senderType || 'Admin',
        IsRead: false,
        ConversationId: conversationId,
        SendEmail: messageData.sendEmail || false
      };
      const response = await fetch(listUrl, {
        method: 'POST',
        headers: { ...headers, 'X-RequestDigest': formDigest },
        body: JSON.stringify(itemPayload)
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('SharePoint error:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.d;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  // ✅ NEW — updates a message record with file attachment metadata after upload
  async updateMessageFileUrl(messageId, fileUrl, fileName, fileSize) {
    if (this._getClientSession()) {
      return await this._clientFn('updateMessageFileUrl', { messageId, fileUrl, fileName, fileSize });
    }
    try {
      const headers = await this.getAuthHeaders();
      const formDigest = await this.getFormDigest();
      const itemUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.clientMessagesListName}')/items(${messageId})`;
      const response = await fetch(itemUrl, {
        method: 'POST',
        headers: {
          ...headers,
          'X-RequestDigest': formDigest,
          'X-HTTP-Method': 'MERGE',
          'IF-MATCH': '*'
        },
        body: JSON.stringify({
          __metadata: { type: 'SP.Data.Client_x0020_MessagesListItem' },
          FileUrl: fileUrl || '',
          FileSize: fileSize || 0
        })
      });
      if (!response.ok && response.status !== 204) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return true;
    } catch (error) {
      console.error('Error updating message file url:', error);
      return false;
    }
  }

  async markMessageRead(messageId) {
    if (this._getClientSession()) {
      try { return await this._clientFn('markMessageRead', { messageId }); }
      catch (e) { return false; }
    }
    try {
      const headers = await this.getAuthHeaders();
      const formDigest = await this.getFormDigest();
      const itemUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.clientMessagesListName}')/items(${messageId})`;
      const response = await fetch(itemUrl, {
        method: 'POST',
        headers: {
          ...headers,
          'X-RequestDigest': formDigest,
          'X-HTTP-Method': 'MERGE',
          'IF-MATCH': '*'
        },
        body: JSON.stringify({
          __metadata: { type: 'SP.Data.Client_x0020_MessagesListItem' },
          IsRead: true
        })
      });
      if (!response.ok && response.status !== 204) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return true;
    } catch (error) {
      console.error('Error marking message read:', error);
      throw error;
    }
  }

  async getUnreadMessageCount() {
    try {
      const headers = await this.getAuthHeaders();
      const listUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.clientMessagesListName}')/items` +
        `?$filter=IsRead eq false and SenderType eq 'Client'&$select=Id&$top=500`;
      const response = await fetch(listUrl, { method: 'GET', headers });
      if (!response.ok) return 0;
      const data = await response.json();
      return (data.d?.results || []).length;
    } catch (error) {
      console.error('Error getting unread count:', error);
      return 0;
    }
  }

  async getClientUnreadCount(clientName) {
    if (this._getClientSession()) {
      try { const r = await this._clientFn('getUnreadCount'); return r.count || 0; }
      catch { return 0; }
    }
    try {
      const headers = await this.getAuthHeaders();
      const safeName = this._escapeOData(clientName);
      const listUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.clientMessagesListName}')/items` +
        `?$filter=ClientName eq '${safeName}' and IsRead eq false and SenderType eq 'Admin'` +
        `&$select=Id&$top=500`;
      const response = await fetch(listUrl, { method: 'GET', headers });
      if (!response.ok) return 0;
      const data = await response.json();
      return (data.d?.results || []).length;
    } catch (error) {
      console.error('Error getting client unread count:', error);
      return 0;
    }
  }

  // ========== BROADCAST MESSAGES ==========
  // VERIFIED internal names:
  // Title, MessageSubject(lowercase!), MessageContent,
  // RecipientType=Choice(6), SentStatus=Choice(6), SentCount=Integer(9)
  // VERIFIED type: SP.Data.Client_x0020_MessagesListItem
  async getAnnouncements() {
    try {
      const headers = await this.getAuthHeaders();
      const listUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.broadcastMessagesListName}')/items` +
        `?$select=Id,Title,MessageSubject,MessageContent,RecipientType,SentStatus,SentCount,ScheduledDate,AttachmentUrl,AttachmentName,Created,Author/Title` +
        `&$expand=Author&$orderby=Created desc&$top=100`;
      const response = await fetch(listUrl, { method: 'GET', headers });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      return data.d.results.map(a => {
        const rawContent = a.MessageContent || '';
        const recipientMarker = '\n---RECIPIENTS---\n';
        const markerIndex = rawContent.indexOf(recipientMarker);
        const displayMessage = markerIndex >= 0
          ? rawContent.substring(0, markerIndex)
          : rawContent;
        const recipientNames = markerIndex >= 0
          ? this._parseJSON(rawContent.substring(markerIndex + recipientMarker.length), null)
          : null;
        return {
          ...a,
          Subject: a.MessageSubject || a.Title || '',
          DisplayMessage: displayMessage,
          RecipientNames: recipientNames,
          RecipientDisplay: recipientNames
            ? recipientNames.join(', ')
            : (a.RecipientType || 'All Clients'),
          AuthorName: a.Author?.Title || 'Admin',
          AttachmentUrl: a.AttachmentUrl || null,
          AttachmentName: a.AttachmentName || null
        };
      });
    } catch (error) {
      console.error('Error fetching announcements:', error);
      return [];
    }
  }

  async getClientAnnouncements(clientName) {
    if (this._getClientSession()) {
      try { return await this._clientFn('getAnnouncements'); }
      catch (e) { console.error('getAnnouncements error:', e); return []; }
    }
    try {
      const all = await this.getAnnouncements();
      return all.filter(a => {
        if (!a.RecipientNames) return true;
        return a.RecipientNames.includes(clientName);
      });
    } catch (error) {
      console.error('Error fetching client announcements:', error);
      return [];
    }
  }

  async sendBroadcastMessage(messageData) {
    try {
      const headers = await this.getAuthHeaders();
      const formDigest = await this.getFormDigest();
      const listUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.broadcastMessagesListName}')/items`;
      const selectedNames = messageData.selectedClientNames || [];
      const isSpecific = selectedNames.length > 0;
      const messageContent = isSpecific
        ? `${messageData.message || ''}\n---RECIPIENTS---\n${JSON.stringify(selectedNames)}`
        : (messageData.message || '');

      // ── Handle attachment ──────────────────────────────────────
      let attachmentUrl = '';
      let attachmentName = '';
      let attachmentBase64 = '';
      const attachFile = messageData.attachmentFile;

      if (attachFile) {
        attachmentName = attachFile.name.replace(/[#%&+~{}]/g, '_');
        const arrayBuffer = await attachFile.arrayBuffer();
        const uint8 = new Uint8Array(arrayBuffer);
        attachmentBase64 = btoa(uint8.reduce((data, byte) => data + String.fromCharCode(byte), ''));

        // Upload to each specific client's Correspondence folder
        if (isSpecific) {
          for (const clientName of selectedNames) {
            try {
              const safeName = clientName.replace(/[#%&+~{}]/g, '_');
              const uploadUrl = `${SHAREPOINT_CONFIG.siteUrl}/_api/web/GetFolderByServerRelativeUrl` +
                `('Client Folders/${encodeURIComponent(safeName)}/Correspondence')` +
                `/Files/add(url='${encodeURIComponent(attachmentName)}',overwrite=true)`;
              const uploadRes = await fetch(uploadUrl, {
                method: 'POST',
                headers: { ...headers, 'X-RequestDigest': formDigest },
                body: arrayBuffer
              });
              if (uploadRes.ok) {
                const uploadData = await uploadRes.json();
                if (!attachmentUrl) attachmentUrl = uploadData.d?.ServerRelativeUrl || '';
              }
            } catch (e) { console.warn(`File upload for ${clientName}:`, e); }
          }
        }
      }

      // ── Save announcement ──────────────────────────────────────
      const itemPayload = {
        __metadata: { type: 'SP.Data.Broadcast_x0020_MessagesListItem' },
        Title: messageData.subject || '',
        MessageSubject: messageData.subject || '',
        MessageContent: messageContent,
        RecipientType: 'All',
        SentStatus: 'Sent',
        SentCount: isSpecific ? selectedNames.length : 0,
        AttachmentUrl: attachmentUrl || '',
        AttachmentName: attachmentName || ''
      };
      const response = await fetch(listUrl, {
        method: 'POST',
        headers: { ...headers, 'X-RequestDigest': formDigest },
        body: JSON.stringify(itemPayload)
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('SharePoint error:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      // ── Send emails via Microsoft Graph API ───────────────────
      if (messageData.sendEmail) {
        const token = await this.getGraphToken();
        let emailList = [];

        if (isSpecific) {
          emailList = (messageData.recipients || [])
            .filter(r => r.ClientEmail || r.email)
            .map(r => r.ClientEmail || r.email);
        } else {
          try {
            const clients = await this.getClients();
            emailList = clients.map(c => c.Email).filter(Boolean);
          } catch (e) { console.warn('Could not fetch clients for email:', e); }
        }

        const emailBody = messageData.message;

        for (const emailAddr of emailList) {
          if (!emailAddr || !emailAddr.includes('@')) continue;
          try {
            const mailPayload = {
              message: {
                subject: messageData.subject || 'Announcement from your CPA',
                from: { emailAddress: { name: 'Management', address: 'noreply@abctaxadvisory.com.au' } },
                body: { contentType: 'Text', content: emailBody },
                toRecipients: [{ emailAddress: { address: emailAddr } }],
                attachments: attachmentBase64 ? [{
                  '@odata.type': '#microsoft.graph.fileAttachment',
                  name: attachmentName,
                  contentBytes: attachmentBase64
                }] : []
              },
              saveToSentItems: false
            };
            await fetch('https://graph.microsoft.com/v1.0/me/sendMail', {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify(mailPayload)
            });
          } catch (e) { console.warn(`Email to ${emailAddr}:`, e); }
        }
      }

      return data.d;
    } catch (error) {
      console.error('Error sending broadcast:', error);
      throw error;
    }
  }

  async deleteAnnouncement(id) {
    try {
      const headers = await this.getAuthHeaders();
      const formDigest = await this.getFormDigest();
      const itemUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.broadcastMessagesListName}')/items(${id})`;
      const response = await fetch(itemUrl, {
        method: 'POST',
        headers: {
          ...headers,
          'X-RequestDigest': formDigest,
          'X-HTTP-Method': 'DELETE',
          'IF-MATCH': '*'
        }
      });
      if (!response.ok && response.status !== 204) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return true;
    } catch (error) {
      console.error('Error deleting announcement:', error);
      throw error;
    }
  }

  // ========== CLIENT FILES ==========
  // Client Files/
  // └── {ClientName}/
  //     ├── Completed Returns/
  //     ├── Correspondence/
  //     └── Uploaded Documents/
  async getAllClientFolders() {
    try {
      const headers = await this.getAuthHeaders();
      const libraryPath = `/sites/TaxClientManagement/${SHAREPOINT_CONFIG.filesLibrary}`;
      const url = `${SHAREPOINT_CONFIG.siteUrl}/_api/web/` +
        `GetFolderByServerRelativeUrl('${libraryPath}')/Folders` +
        `?$select=Name,ServerRelativeUrl,ItemCount&$orderby=Name asc&$top=500`;
      const response = await fetch(url, { method: 'GET', headers });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      return (data.d.results || []).filter(f =>
        !f.Name.startsWith('_') && f.Name !== 'Forms'
      );
    } catch (error) {
      console.error('Error fetching all client folders:', error);
      return [];
    }
  }

  async getClientFiles(clientName) {
    if (this._getClientSession()) {
      try { return await this._clientFn('getFiles'); }
      catch (e) { console.error('getFiles error:', e); return []; }
    }
    try {
      const headers = await this.getAuthHeaders();
      const allFiles = [];

      const fetchFolder = async (folderPath, category, recurse) => {
        // Fetch files directly in this folder
        const filesUrl = `${SHAREPOINT_CONFIG.siteUrl}/_api/web/` +
          `GetFolderByServerRelativeUrl('${folderPath.replace(/'/g, "''")}')/Files` +
          `?$select=Name,ServerRelativeUrl,TimeCreated,TimeLastModified,Length`;
        const fRes = await fetch(filesUrl, { method: 'GET', headers });
        if (fRes.ok) {
          const fData = await fRes.json();
          (fData.d.results || []).forEach(f => allFiles.push({
            Name: f.Name,
            FileUrl: f.ServerRelativeUrl,
            Category: category,
            Size: parseInt(f.Length) || 0,
            Created: f.TimeCreated,
            Modified: f.TimeLastModified,
            ClientName: clientName
          }));
        }
        // Recurse into subfolders (receipt subfolders inside Uploaded Documents)
        if (recurse) {
          const subUrl = `${SHAREPOINT_CONFIG.siteUrl}/_api/web/` +
            `GetFolderByServerRelativeUrl('${folderPath.replace(/'/g, "''")}')/Folders` +
            `?$select=Name,ServerRelativeUrl`;
          const sRes = await fetch(subUrl, { method: 'GET', headers });
          if (sRes.ok) {
            const sData = await sRes.json();
            for (const sub of (sData.d.results || [])) {
              await fetchFolder(sub.ServerRelativeUrl, category, false);
            }
          }
        }
      };

      for (const folder of SHAREPOINT_CONFIG.fileSubfolders) {
        const folderPath = `/sites/TaxClientManagement/${SHAREPOINT_CONFIG.filesLibrary}/${clientName}/${folder}`;
        await fetchFolder(folderPath, folder, folder === 'Uploaded Documents');
      }
      return allFiles;
    } catch (error) {
      console.error('Error fetching client files:', error);
      return [];
    }
  }

  async getClientFolderStructure(clientName) {
    try {
      // Uses SP REST API with the SharePoint MSAL token (AllSites.FullControl scope).
      // Returns ServerRelativeUrl as FileUrl — admin downloads route through
      // the get-file.js Netlify proxy using path= which has its own Graph credentials.
      const headers = await this.getAuthHeaders();
      const structure = {
        'Completed Returns': [],
        'Correspondence': [],
        'Uploaded Documents': []
      };
      for (const folder of SHAREPOINT_CONFIG.fileSubfolders) {
        const folderPath = `/sites/TaxClientManagement/${SHAREPOINT_CONFIG.filesLibrary}/${clientName}/${folder}`;
        const url = `${SHAREPOINT_CONFIG.siteUrl}/_api/web/` +
          `GetFolderByServerRelativeUrl('${folderPath.replace(/'/g, "''")}')/Files` +
          `?$select=Name,ServerRelativeUrl,TimeCreated,TimeLastModified,Length`;
        const response = await fetch(url, { method: 'GET', headers });
        if (response.ok) {
          const data = await response.json();
          structure[folder] = (data.d.results || []).map(f => ({
            Name: f.Name,
            FileUrl: f.ServerRelativeUrl,
            Category: folder,
            Size: parseInt(f.Length) || 0,
            Created: f.TimeCreated,
            Modified: f.TimeLastModified,
            ClientName: clientName
          }));
        }
      }
      return structure;
    } catch (error) {
      console.error('Error fetching folder structure:', error);
      return { 'Completed Returns': [], 'Correspondence': [], 'Uploaded Documents': [] };
    }
  }

  async ensureFolderExists(serverRelativeUrl) {
    try {
      const headers = await this.getAuthHeaders();
      const encodedPath = serverRelativeUrl.replace(/'/g, "''");
      const checkUrl = `${SHAREPOINT_CONFIG.siteUrl}/_api/web/GetFolderByServerRelativeUrl('${encodedPath}')`;
      const checkRes = await fetch(checkUrl, { method: 'GET', headers });
      if (checkRes.ok) return;
      const formDigest = await this.getFormDigest();
      const createRes = await fetch(`${SHAREPOINT_CONFIG.siteUrl}/_api/web/folders`, {
        method: 'POST',
        headers: { ...headers, 'X-RequestDigest': formDigest },
        body: JSON.stringify({
          '__metadata': { 'type': 'SP.Folder' },
          'ServerRelativeUrl': serverRelativeUrl
        })
      });
      if (!createRes.ok && createRes.status !== 409) {
        const err = await createRes.text();
        console.warn(`Could not create folder "${serverRelativeUrl}":`, err);
      }
    } catch (error) {
      console.warn('ensureFolderExists error:', error);
    }
  }

  async uploadDocument(file, metadata) {
    if (this._getClientSession()) {
      // Convert file to base64 for transmission to Netlify Function
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = e => resolve(e.target.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      return await this._clientFn('uploadDocument', {
        fileName: file.name,
        fileBase64: base64,
        category: metadata.category || 'Uploaded Documents',
        clientName: metadata.clientName
      });
    }
    try {
      const headers = await this.getAuthHeaders();
      const formDigest = await this.getFormDigest();
      const subFolder = metadata.category || 'Uploaded Documents';
      const clientFolderPath =
        `/sites/TaxClientManagement/${SHAREPOINT_CONFIG.filesLibrary}/${metadata.clientName}`;
      const fullFolderPath = `${clientFolderPath}/${subFolder}`;
      // Ensure all intermediate folders (supports nested paths like "Uploaded Documents/Receipt Name")
      await this.ensureFolderExists(clientFolderPath);
      const parts = subFolder.split('/');
      let builtPath = clientFolderPath;
      for (const part of parts) {
        builtPath = `${builtPath}/${part}`;
        await this.ensureFolderExists(builtPath);
      }
      const safeFileName = file.name.replace(/[#%&+~{}]/g, '_');
      const uploadUrl = `${SHAREPOINT_CONFIG.siteUrl}/_api/web/` +
        `GetFolderByServerRelativeUrl('${fullFolderPath}')/` +
        `Files/add(url='${encodeURIComponent(safeFileName)}',overwrite=true)`;
      const fileBuffer = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = e => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      });
      const uploadResponse = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Authorization': headers['Authorization'],
          'X-RequestDigest': formDigest,
          'Accept': 'application/json;odata=verbose'
        },
        body: fileBuffer
      });
      if (!uploadResponse.ok) {
        const err = await uploadResponse.text();
        console.error('Upload error response from SharePoint:', err);
        throw new Error(`Upload failed: ${uploadResponse.status}`);
      }
      let fileUrl = `${fullFolderPath}/${safeFileName}`;
      try {
        const uploadData = await uploadResponse.json();
        fileUrl = uploadData.d?.ServerRelativeUrl || fileUrl;
      } catch {
        console.warn('Upload response was not JSON — file uploaded OK, using constructed URL.');
      }
      return {
        Name: file.name,
        FileUrl: fileUrl,
        Category: subFolder,
        ClientName: metadata.clientName,
        Size: file.size,
        Created: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  }

  async deleteDocument(serverRelativeUrl, driveId, driveItemId) {
    if (this._getClientSession()) {
      // Try to get driveId/driveItemId from file URL stored in session cache
      // Client pages pass fileToDelete.FileUrl — we need to look up the drive info
      // from the cached file list or from the file object itself
      // Since we can't easily look up by URL, we accept them as extra params
      // ClientDocuments.vue needs to be updated to pass file object
      // For now, skip delete gracefully if no drive info (file stays in SP but UI removes it)
      if (driveId && driveItemId) {
        return await this._clientFn('deleteDocument', { driveId, driveItemId });
      }
      console.warn('deleteDocument: no driveId/driveItemId for client, skipping server delete');
      return true; // UI will still remove it from the list
    }
    try {
      const headers = await this.getAuthHeaders();
      const formDigest = await this.getFormDigest();
      const url = `${SHAREPOINT_CONFIG.siteUrl}/_api/web/` +
        `GetFileByServerRelativeUrl('${serverRelativeUrl}')`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...headers,
          'X-RequestDigest': formDigest,
          'X-HTTP-Method': 'DELETE',
          'IF-MATCH': '*'
        }
      });
      if (!response.ok && response.status !== 204) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return true;
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  }

  // ========== CLIENT INVITATIONS ==========
  // VERIFIED type: SP.Data.Client_x0020_InvitationsListItem
  // Required columns: Title, Email, InviteToken, Status, ExpiryDate, SentBy
  async sendClientInvitation(inviteData) {
    try {
      const headers = await this.getAuthHeaders();
      const formDigest = await this.getFormDigest();
      const listUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.invitationsListName}')/items`;
      const token = Math.random().toString(36).substring(2, 15) +
        Math.random().toString(36).substring(2, 15);
      const expiry = new Date();
      expiry.setDate(expiry.getDate() + 7);
      const response = await fetch(listUrl, {
        method: 'POST',
        headers: { ...headers, 'X-RequestDigest': formDigest },
        body: JSON.stringify({
        __metadata: { type: 'SP.Data.Client_x0020_InvitationsListItem' },
        Title: inviteData.clientName,
        Email: inviteData.email,
        InviteToken: token,
        InvitationStatus: 'Pending',
        ExpiryDate: expiry.toISOString(),
        DateSent: new Date().toISOString(),
        SentBy: inviteData.sentBy || 'Admin'
      })
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      return { ...data.d, token };
    } catch (error) {
      console.error('Error sending invitation:', error);
      throw error;
    }
  }

  async validateInvitationToken(token) {
  try {
    const headers = await this.getAuthHeaders();
    const safeToken = this._escapeOData(token);
    const listUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.invitationsListName}')/items` +
      `?$select=Id,Title,Email,InviteToken,InvitationStatus,ExpiryDate,SentBy` +
      `&$filter=InviteToken eq '${safeToken}'` +
      `&$top=1`;
    const response = await fetch(listUrl, { method: 'GET', headers });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    const results = data.d?.results || [];
    if (results.length === 0) return { valid: false, reason: 'Token not found.' };

    const invite = results[0];
    if (invite.InvitationStatus === 'Accepted') return { valid: false, reason: 'This invitation has already been used.' };
    if (invite.InvitationStatus === 'Expired') return { valid: false, reason: 'This invitation has expired.' };

    const now = new Date();
    const expiry = new Date(invite.ExpiryDate);
    if (now > expiry) {
      await this._markInvitationExpired(invite.Id);
      return { valid: false, reason: 'This invitation link has expired. Please contact your administrator.' };
    }

    return { valid: true, invite };
  } catch (error) {
    console.error('Error validating invitation token:', error);
    return { valid: false, reason: 'Error validating invitation. Please try again.' };
  }
}

 async acceptInvitation(inviteId, clientName) {
  try {
    const headers = await this.getAuthHeaders();
    const formDigest = await this.getFormDigest();
    const inviteUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.invitationsListName}')/items(${inviteId})`;
    await fetch(inviteUrl, {
      method: 'POST',
      headers: {
        ...headers,
        'X-RequestDigest': formDigest,
        'X-HTTP-Method': 'MERGE',
        'IF-MATCH': '*'
      },
      body: JSON.stringify({
        __metadata: { type: 'SP.Data.Client_x0020_InvitationsListItem' },
        InvitationStatus: 'Accepted'
      })
    });
    const safeClientName = this._escapeOData(clientName);
    const clientSearchUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.clientsListName}')/items` +
      `?$select=Id&$filter=Title eq '${safeClientName}'&$top=1`;
    const clientRes = await fetch(clientSearchUrl, { method: 'GET', headers });
    if (clientRes.ok) {
      const clientData = await clientRes.json();
      const clientResults = clientData.d?.results || [];
      if (clientResults.length > 0) {
        const clientId = clientResults[0].Id;
        await this.updateClient(clientId, { portalAccessSent: true });
      }
    }
    return { success: true };
  } catch (error) {
    console.error('Error accepting invitation:', error);
    throw error;
  }
}

 async _markInvitationExpired(inviteId) {
  try {
    const headers = await this.getAuthHeaders();
    const formDigest = await this.getFormDigest();
    const inviteUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.invitationsListName}')/items(${inviteId})`;
    await fetch(inviteUrl, {
      method: 'POST',
      headers: {
        ...headers,
        'X-RequestDigest': formDigest,
        'X-HTTP-Method': 'MERGE',
        'IF-MATCH': '*'
      },
      body: JSON.stringify({
        __metadata: { type: 'SP.Data.Client_x0020_InvitationsListItem' },
        InvitationStatus: 'Expired'  // ✅ FIXED: was Status
      })
    });
  } catch (error) {
    console.warn('Could not mark invitation expired:', error);
  }
}


  // ========== CLIENT APPOINTMENTS ==========
  // Required columns: Title, ClientName, AppointmentDate, StartTime,
  // EndTime, MeetingType, AssigneeName, Status, Notes
  // Type: SP.Data.Client_x0020_AppointmentsListItem
  async getClientAppointments(clientName) {
    if (this._getClientSession()) {
      try { return await this._clientFn('getAppointments'); }
      catch (e) { console.error('getAppointments error:', e); return []; }
    }
    try {
      const headers = await this.getAuthHeaders();
      const safeName = this._escapeOData(clientName);
      const listUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.appointmentsListName}')/items` +
        `?$select=Id,Title,ClientName,AppointmentDate,StartTime,EndTime,MeetingType,AssigneeName,Status,Notes,Created` +
        `&$filter=ClientName eq '${safeName}'` +
        `&$orderby=AppointmentDate asc&$top=200`;
      const response = await fetch(listUrl, { method: 'GET', headers });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      return data.d.results;
    } catch (error) {
      console.error('Error fetching client appointments:', error);
      return [];
    }
  }

  async getAllAppointments() {
    try {
      const headers = await this.getAuthHeaders();
      const listUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.appointmentsListName}')/items` +
        `?$select=Id,Title,ClientName,AppointmentDate,StartTime,EndTime,MeetingType,AssigneeName,Status,Notes,Created` +
        `&$orderby=AppointmentDate asc&$top=500`;
      const response = await fetch(listUrl, { method: 'GET', headers });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      return data.d.results;
    } catch (error) {
      console.error('Error fetching all appointments:', error);
      return [];
    }
  }

  async createAppointment(appointmentData) {
    if (this._getClientSession()) {
      return await this._clientFn('createAppointment', {
        title: appointmentData.title,
        appointmentDate: appointmentData.appointmentDate,
        startTime: appointmentData.startTime,
        endTime: appointmentData.endTime,
        meetingType: appointmentData.meetingType,
        assigneeName: appointmentData.assigneeName,
        notes: appointmentData.notes
      });
    }
    try {
      const headers = await this.getAuthHeaders();
      const formDigest = await this.getFormDigest();
      const listUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.appointmentsListName}')/items`;
      const itemPayload = {
        __metadata: { type: 'SP.Data.Client_x0020_AppointmentsListItem' },
        Title: appointmentData.title || 'Appointment',
        ClientName: appointmentData.clientName || '',
        AppointmentDate: appointmentData.appointmentDate || null,
        StartTime: appointmentData.startTime || '',
        EndTime: appointmentData.endTime || '',
        MeetingType: appointmentData.meetingType || 'Video Call',
        AssigneeName: appointmentData.assigneeName || '',
        Status: 'Scheduled',
        Notes: appointmentData.notes || ''
      };
      const response = await fetch(listUrl, {
        method: 'POST',
        headers: { ...headers, 'X-RequestDigest': formDigest },
        body: JSON.stringify(itemPayload)
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('SharePoint error:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.d;
    } catch (error) {
      console.error('Error creating appointment:', error);
      throw error;
    }
  }

  async updateAppointment(appointmentId, appointmentData) {
    try {
      const headers = await this.getAuthHeaders();
      const formDigest = await this.getFormDigest();
      const itemUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.appointmentsListName}')/items(${appointmentId})`;
      const itemPayload = {
        __metadata: { type: 'SP.Data.Client_x0020_AppointmentsListItem' }
      };
      if (appointmentData.title) itemPayload.Title = appointmentData.title;
      if (appointmentData.appointmentDate !== undefined) itemPayload.AppointmentDate = appointmentData.appointmentDate;
      if (appointmentData.startTime) itemPayload.StartTime = appointmentData.startTime;
      if (appointmentData.endTime) itemPayload.EndTime = appointmentData.endTime;
      if (appointmentData.meetingType) itemPayload.MeetingType = appointmentData.meetingType;
      if (appointmentData.assigneeName) itemPayload.AssigneeName = appointmentData.assigneeName;
      if (appointmentData.status) itemPayload.Status = appointmentData.status;
      if (appointmentData.notes !== undefined) itemPayload.Notes = appointmentData.notes;
      const response = await fetch(itemUrl, {
        method: 'POST',
        headers: {
          ...headers,
          'X-RequestDigest': formDigest,
          'X-HTTP-Method': 'MERGE',
          'IF-MATCH': '*'
        },
        body: JSON.stringify(itemPayload)
      });
      if (!response.ok && response.status !== 204) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return { success: true };
    } catch (error) {
      console.error('Error updating appointment:', error);
      throw error;
    }
  }

  async cancelAppointment(appointmentId) {
    if (this._getClientSession()) {
      return await this._clientFn('cancelAppointment', { appointmentId });
    }
    try {
      return await this.updateAppointment(appointmentId, { status: 'Cancelled' });
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      throw error;
    }
  }

  async deleteAppointment(appointmentId) {
    try {
      const headers = await this.getAuthHeaders();
      const formDigest = await this.getFormDigest();
      const itemUrl = `${this.baseUrl}/getbytitle('${SHAREPOINT_CONFIG.appointmentsListName}')/items(${appointmentId})`;
      const response = await fetch(itemUrl, {
        method: 'POST',
        headers: {
          ...headers,
          'X-RequestDigest': formDigest,
          'X-HTTP-Method': 'DELETE',
          'IF-MATCH': '*'
        }
      });
      if (!response.ok && response.status !== 204) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return true;
    } catch (error) {
      console.error('Error deleting appointment:', error);
      throw error;
    }
  }
}

export default new SharePointService();